import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, Shield, Heart, Target } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen bg-qc-bg text-qc-text">
      <div 
        className="px-5 py-3 flex items-center max-w-2xl mx-auto"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
      >
        <Link
          to="/login"
          data-testid="about-back-button"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-base font-extrabold text-qc-text ml-3">About</h1>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6" data-testid="about-page">
        {/* Hero */}
        <div className="mb-10">
          <h1 className="text-4xl font-black text-qc-text mb-3">
            <span className="text-qc-green">Get Green Light</span>
          </h1>
          <p className="text-xl text-qc-text-dim leading-relaxed">
            Scan. Decide. Move on.
          </p>
        </div>

        {/* Mission */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-qc-text mb-3">Our Mission</h2>
          <p className="text-qc-text-dim leading-relaxed">
            We believe making healthy food choices shouldn&apos;t require a nutrition degree. 
            Green Light uses AI to give you instant, personalized verdicts on any grocery 
            product — so you can shop with confidence, not confusion.
          </p>
        </div>

        {/* How it works */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-qc-text mb-4">How It Works</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-qc-green flex items-center justify-center flex-shrink-0">
                <span className="text-qc-bg font-black text-sm">1</span>
              </div>
              <div>
                <h3 className="font-bold text-qc-text mb-1">Tell us your goals</h3>
                <p className="text-sm text-qc-text-dim">
                  Set up your health profile with dietary preferences, restrictions, and goals.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-qc-green flex items-center justify-center flex-shrink-0">
                <span className="text-qc-bg font-black text-sm">2</span>
              </div>
              <div>
                <h3 className="font-bold text-qc-text mb-1">Scan any product</h3>
                <p className="text-sm text-qc-text-dim">
                  Point your camera at a product label or nutrition facts.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-qc-green flex items-center justify-center flex-shrink-0">
                <span className="text-qc-bg font-black text-sm">3</span>
              </div>
              <div>
                <h3 className="font-bold text-qc-text mb-1">Get your verdict</h3>
                <p className="text-sm text-qc-text-dim">
                  Receive an instant GO, MAYBE, or HELL NO — plus reasons and better alternatives.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-qc-text mb-4">What We Stand For</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-qc-surface rounded-2xl border border-qc-border p-4">
              <Zap className="w-6 h-6 text-qc-green mb-2" />
              <h3 className="font-bold text-qc-text text-sm mb-1">Speed</h3>
              <p className="text-xs text-qc-text-dim">Answers in seconds, not minutes.</p>
            </div>
            <div className="bg-qc-surface rounded-2xl border border-qc-border p-4">
              <Target className="w-6 h-6 text-qc-amber mb-2" />
              <h3 className="font-bold text-qc-text text-sm mb-1">Personalization</h3>
              <p className="text-xs text-qc-text-dim">Tailored to your unique health needs.</p>
            </div>
            <div className="bg-qc-surface rounded-2xl border border-qc-border p-4">
              <Shield className="w-6 h-6 text-qc-green mb-2" />
              <h3 className="font-bold text-qc-text text-sm mb-1">Privacy</h3>
              <p className="text-xs text-qc-text-dim">Your data stays yours.</p>
            </div>
            <div className="bg-qc-surface rounded-2xl border border-qc-border p-4">
              <Heart className="w-6 h-6 text-qc-red mb-2" />
              <h3 className="font-bold text-qc-text text-sm mb-1">Honesty</h3>
              <p className="text-xs text-qc-text-dim">Straight talk, no hidden agendas.</p>
            </div>
          </div>
        </div>

        {/* Footer links */}
        <div className="border-t border-qc-border pt-6">
          <p className="text-sm text-qc-text-dim mb-4">
            Questions? We&apos;re here to help.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="text-qc-green font-bold text-sm hover:text-qc-green-dark">
              Contact Us
            </Link>
            <Link to="/terms" className="text-qc-text-dim font-medium text-sm hover:text-qc-text">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-qc-text-dim font-medium text-sm hover:text-qc-text">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
